/**
 * DOCUMENTATION:
 * Component: CleanCodeFormatting
 * Purpose: Teaching the philosophy of meaningful comments and vertical/horizontal formatting.
 * * * Note for Junior Developers:
 * - Rule 1: Don't comment bad code—rewrite it.
 * - Rule 2: Comments should explain the "Why" (intent), not the "What" (logic).
 * - Rule 3: Vertical Formatting (The Newspaper Metaphor). High-level concepts at the top,
 * details at the bottom.
 * - Rule 4: Use vertical whitespace to separate "thoughts" in your functions, just like
 * paragraphs in a book.
 */

import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
} from "@mui/material";

/* ✅ Icons */
import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1516116216624-53e697fedbea";

/* ✅ Styled Components */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeBlock = styled("pre")({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
});

const ResponsiveDataRow = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "1.5fr 2fr",
    gap: "15px",
    padding: "12px",
    borderBottom: "1px solid rgba(255,255,255,0.1)",
    "&:last-child": { borderBottom: "none" },
    [theme.breakpoints.down("md")]: {
        gridTemplateColumns: "1fr",
        gap: "5px",
    },
}));

/* ✅ Main Component */
export default function CleanCodeFormatting() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [copiedIndex, setCopiedIndex] = useState(null);

    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    const formattingRules = useMemo(() => [
        { rule: "The Newspaper Metaphor", description: "A source file should be like a newspaper article. Title/Headline at the top, high-level summary next, and all the nitty-gritty details at the end." },
        { rule: "Vertical Density", description: "Lines of code that are tightly related should appear vertically close to each other. Don't force the reader to scroll 100 lines to find a variable definition." },
        { rule: "Intentional Comments", description: "Only use comments for legal requirements, warnings of consequences (e.g., 'Do not run this without VPN'), or explaining a complex 'Why' that code cannot express." },
        { rule: "Avoid Noise Comments", description: "Remove redundant comments like '// set name to kofi'. If the code says 'user.name = \"kofi\"', the comment is just clutter." },
    ], []);

    const codingModules = useMemo(() => [
        {
            title: "1. Refactoring 'What' Comments into Code",
            code: `// ❌ JUNIOR: Using a comment to explain messy logic\n// Check to see if the employee is eligible for full benefits\nif (employee.flags & HOURLY && employee.age > 65) { ... }\n\n// ✅ SENIOR: The logic is extracted into an intention-revealing variable\nconst isEligibleForFullBenefits = employee.isHourly() && employee.isSenior();\n\nif (isEligibleForFullBenefits) { ... }`,
            desc: "Instead of writing a comment to explain what a complex 'if' statement does, move that logic into a variable or function with a clear name. The code now documents itself."
        },
        {
            title: "2. Vertical Formatting & Density",
            code: `// ❌ BAD: No spacing makes it hard to distinguish 'paragraphs' of logic\nfunction signup(user) {\n  validate(user);\n  const hash = encrypt(user.password);\n  user.password = hash;\n  db.save(user);\n  email.sendWelcome(user);\n}\n\n// ✅ GOOD: Spacing separates logical steps\nfunction signup(user) {\n  validate(user);\n\n  const hashedPassword = encrypt(user.password);\n  user.password = hashedPassword;\n\n  db.save(user);\n  email.sendWelcome(user);\n}`,
            desc: "Vertical whitespace is a visual cue. It tells the reader: 'This group of lines belongs together, and the next group is a new step.' It makes the function infinitely more scannable."
        },
        {
            title: "3. Explaining 'Why' (The Only Good Comment)",
            code: `// ✅ GOOD: Explaining a non-obvious business decision\n// We use a 13ms delay here because the legacy hardware controller\n// fails to register pulses sent faster than 15ms.\nconst HARDWARE_PULSE_LATENCY = 13;\nawait sleep(HARDWARE_PULSE_LATENCY);`,
            desc: "Code can tell you *how* something works, but it rarely tells you *why* a specific, weird requirement exists. These are the only comments you should keep."
        }
    ], []);

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography variant={isMobile ? "h5" : "h3"} sx={{ fontWeight: 800, color: "goldenrod" }}>
                    COMMENTS & FORMATTING
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                <Grid item xs={12}>
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            The Newspaper Metaphor
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.9, lineHeight: 1.6 }}>
                            A source file should be organized like a high-quality newspaper. The top of the file should contain the high-level concepts and entry points. As you scroll down, the level of detail should increase until you reach the lowest-level helper functions at the very bottom.
                        </Typography>



                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 4, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "goldenrod", color: "#100720" }}><AiFillBulb size={24} /></Avatar>
                            <Typography variant="h6" fontWeight={700}>Professional Formatting Rules</Typography>
                        </Box>

                        <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "10px", overflow: "hidden" }}>
                            {formattingRules.map((item, i) => (
                                <ResponsiveDataRow key={i}>
                                    <Box sx={{ fontWeight: "bold", color: "goldenrod" }}>{item.rule}</Box>
                                    <Box sx={{ opacity: 0.8, fontSize: "0.85rem" }}>{item.description}</Box>
                                </ResponsiveDataRow>
                            ))}
                        </Box>
                    </SectionCard>
                </Grid>

                {codingModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                <Typography variant="h6" fontWeight={700}>{item.title}</Typography>
                                <Stack direction="row" spacing={1}>
                                    <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                        <Button size="small" variant="outlined" onClick={() => handleCopy(item.code, i)} sx={{ color: "white", borderColor: "rgba(255,255,255,0.3)" }}>
                                            {copiedIndex === i ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </Button>
                                    </Tooltip>
                                    <Link to="/compiler" style={{ textDecoration: "none" }}>
                                        <Button size="small" variant="outlined" startIcon={<CodeIcon />} sx={{ color: "goldenrod", borderColor: "goldenrod" }}>
                                            Try it
                                        </Button>
                                    </Link>
                                </Stack>
                            </Box>
                            <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>{item.desc}</Typography>
                            <CodeBlock>{item.code}</CodeBlock>
                        </SectionCard>
                    </Grid>
                ))}

                <Grid item xs={12} textAlign="center">
                    <Link to="/clean-code-dashboard" style={{ textDecoration: "none" }}>
                        <ActionButton variant="contained">BACK TO DASHBOARD</ActionButton>
                    </Link>
                </Grid>
            </Grid>

            <Zoom in={true}>
                <Fab onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} size="small" sx={{ position: "fixed", bottom: 25, right: 25, bgcolor: "goldenrod" }}>
                    <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 800,
    padding: "12px 40px",
    borderRadius: "999px",
    textTransform: "uppercase",
    "&:hover": { backgroundColor: "#e0b200" },
}));
